import {
  STATURA_CARE_CUSTOMER_DPA_VERSION,
  STATURA_CARE_CUSTOMER_MSA_VERSION,
  STATURA_CARE_CUSTOMER_SIGNATORY_AUTHORITY_VERSION,
  STATURA_CARE_CUSTOMER_SUBPROCESSOR_VERSION,
  STATURA_CARE_PUBLIC_URLS,
} from './public-legal';

export type CustomerLegalDocumentType =
  | 'customer_msa'
  | 'customer_data_processing'
  | 'customer_subprocessors'
  | 'customer_signatory_authority';

export interface CustomerLegalDocumentDefinition {
  type: CustomerLegalDocumentType;
  label: string;
  version: string;
  url: string | null;
  acknowledgementLabel: string;
  summary: string;
}

export type CustomerLegalAcceptanceAuditRecord = {
  documentType: CustomerLegalDocumentType;
  version: string;
};

export const CUSTOMER_LEGAL_DOCUMENT_DEFINITIONS: readonly CustomerLegalDocumentDefinition[] = [
  {
    type: 'customer_msa',
    label: 'Master Services Agreement',
    version: STATURA_CARE_CUSTOMER_MSA_VERSION,
    url: STATURA_CARE_PUBLIC_URLS.customerMsa,
    acknowledgementLabel:
      'I have reviewed the current Statura Care Master Services Agreement and agree to continue using the platform on behalf of my organisation under those terms.',
    summary:
      'Sets the commercial SaaS terms, including subscription use rights, provider responsibilities, support boundaries, and liability allocation.',
  },
  {
    type: 'customer_data_processing',
    label: 'Data Processing Schedule',
    version: STATURA_CARE_CUSTOMER_DPA_VERSION,
    url: STATURA_CARE_PUBLIC_URLS.customerDataProcessing,
    acknowledgementLabel:
      'I have reviewed the current Data Processing Schedule and understand how Statura Care processes customer data on behalf of my organisation.',
    summary:
      'Covers privacy roles, security measures, subprocessors, cross-border handling, and data return/deletion commitments for customer data.',
  },
  {
    type: 'customer_subprocessors',
    label: 'Subprocessor Schedule',
    version: STATURA_CARE_CUSTOMER_SUBPROCESSOR_VERSION,
    url: STATURA_CARE_PUBLIC_URLS.customerSubprocessors,
    acknowledgementLabel:
      'I have reviewed the current Subprocessor Schedule and understand which approved third-party service providers support the Statura Care platform.',
    summary:
      'Lists the current approved platform subprocessors evidenced in the live product stack, including hosting, telemetry, messaging, and billing vendors.',
  },
  {
    type: 'customer_signatory_authority',
    label: 'Authorised signatory attestation',
    version: STATURA_CARE_CUSTOMER_SIGNATORY_AUTHORITY_VERSION,
    url: STATURA_CARE_PUBLIC_URLS.customerLegalPack,
    acknowledgementLabel:
      'I confirm that I am authorised to accept these customer terms and related schedules on behalf of my organisation.',
    summary:
      'Records that the accepting tenant administrator is authorised by the customer organisation to bind it to the current customer legal pack.',
  },
] as const;

export function getRequiredCustomerLegalDocuments(): CustomerLegalDocumentDefinition[] {
  return [...CUSTOMER_LEGAL_DOCUMENT_DEFINITIONS];
}

export function getCustomerLegalDocumentDefinition(
  documentType: CustomerLegalDocumentType,
): CustomerLegalDocumentDefinition {
  const definition = CUSTOMER_LEGAL_DOCUMENT_DEFINITIONS.find((item) => item.type === documentType);
  if (!definition) {
    throw new Error(`Unknown customer legal document type: ${documentType}`);
  }
  return definition;
}

export function buildCustomerLegalAcceptanceAction(
  documentType: CustomerLegalDocumentType,
  version: string,
): string {
  return `CUSTOMER_LEGAL_ACCEPTANCE:${documentType}:${version}`;
}

/**
 * Phase 8.7 P2-A — deterministic descriptor of a customer-legal document
 * suitable for hashing at acceptance time. The descriptor binds the row to
 * the specific (type, version, label, acknowledgement, url) tuple defined
 * in this package — so any change to those fields will produce a different
 * hash and force a re-acceptance prompt.
 *
 * Note: this is NOT a hash of the rendered MDX content at statura.care. The
 * full content-hash binding is P3-B in the audit (drift hazard between the
 * version constant and the MDX) and is deferred. This descriptor hash is
 * the audit-row's binding to the published `CustomerLegalDocumentDefinition`
 * at the moment of acceptance.
 */
export function buildCustomerLegalDocumentDescriptor(
  document: CustomerLegalDocumentDefinition,
): string {
  return [
    document.type,
    document.version,
    document.label,
    document.url ?? '',
    document.acknowledgementLabel,
    document.summary,
  ].join('\n');
}

export function parseCustomerLegalAcceptanceAction(
  action: string | null | undefined,
): CustomerLegalAcceptanceAuditRecord | null {
  if (!action) return null;

  const match =
    /^CUSTOMER_LEGAL_ACCEPTANCE:(customer_msa|customer_data_processing|customer_subprocessors|customer_signatory_authority):(.+)$/.exec(
      action,
    );
  if (!match) return null;
  const version = match[2];
  if (!version) return null;

  return {
    documentType: match[1] as CustomerLegalDocumentType,
    version,
  };
}
